<template>
    <div class="address-field-wrapper">
        <input type="text" v-model="location" :class="{ expanded: searchResults && searchResults.length > 0 }" />
        <ul class="autocomplete-results" :class="{ hidden: !searchResults || (searchResults && searchResults.length === 0) }">
            <li class="autocomplete-result" v-for="(result, i) in searchResults" :key="i" @click="storeChosenPlace(result)">{{ result.description }}</li>
        </ul>
        <div ref="test" style="display: none" />
    </div>
</template>

<script>
import { debounce } from "../utils.debounce";
export default {
	name: "address-autocomplete",
	data() {
		return {
			location: "",
			searchResults: [],
			autocompleteService: null,
			awaitingRequest: false,
			chosenLocation: null,
			directionsService: null,
			geocoderService: null,
		};
	},
	props: {
		modelValue: {
			required: false,
			default: () => {},
		},
		origin: {
			required: false,
			default: () => {
				return {
					lat: 0,
					lng: 0,
				};
			},
		},
	},
	mounted() {
		// window.__mvaGoogleMapsIsReady is set by the inline-before script in PHP
		// BEFORE the Maps script tag, so if Maps already fired its callback we
		// can call MapsInit() directly. Otherwise push onto the callbacks queue.
		if (window.__mvaGoogleMapsIsReady || (window.google && window.google.maps && window.google.maps.places)) {
			this.MapsInit();
		} else {
			// Defensive: queue may not exist if the inline script somehow didn't run
			if (!window.__mvaGoogleMapsReadyCallbacks) {
				window.__mvaGoogleMapsReadyCallbacks = [];
			}
			window.__mvaGoogleMapsReadyCallbacks.push(() => {
				this.MapsInit();
			});
		}
	},
	methods: {
		MapsInit() {
			this.autocompleteService =
				new window.google.maps.places.AutocompleteService();
			this.directionsService = new window.google.maps.DistanceMatrixService();
			this.geocoderService = new window.google.maps.Geocoder();
		},
		displaySuggestions(predictions, status) {
			if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
				this.searchResults = [];
				return;
			}
			this.searchResults = predictions.map((prediction) => {
				return { description: prediction.description, id: prediction.place_id };
			});
		},
		storeChosenPlace(place) {
			this.location = place.description;
			this.chosenLocation = place;
			this.searchResults = [];
			this.calculateDistance();
		},
		componentToAddress(components) {
			let returnObject = {};
			for (const component of components) {
				if (component.types.indexOf("street_number") !== -1) {
					returnObject = {
						...returnObject,
						street_number: component.long_name,
					};
				} else if (component.types.indexOf("route") !== -1) {
					returnObject = { ...returnObject, street_name: component.long_name };
				} else if (component.types.indexOf("sublocality") !== -1) {
					returnObject = { ...returnObject, suburb: component.long_name };
				} else if (component.types.indexOf("locality") !== -1) {
					returnObject = { ...returnObject, city: component.long_name };
				} else if (
					component.types.indexOf("administrative_area_level_1") !== -1
				) {
					returnObject = { ...returnObject, province: component.long_name };
				} else if (component.types.indexOf("country") !== -1) {
					returnObject = { ...returnObject, country: component.long_name };
				} else if (component.types.indexOf("postal_code") !== -1) {
					returnObject = { ...returnObject, postal_code: component.long_name };
				}
			}
			return returnObject;
		},
		calculateDistance() {
			this.geocoderService.geocode(
				{
					placeId: this.chosenLocation.id,
					region: "za",
				},
				(geocodeResults) => {
					const geocodedPlace = geocodeResults[0];
					const destinationPlace = new window.google.maps.LatLng(
						geocodedPlace.geometry.location.lat(),
						geocodedPlace.geometry.location.lng(),
					);

					const route = {
						origins: [
							new window.google.maps.LatLng(this.origin.lat, this.origin.lng),
						],
						destinations: [destinationPlace],
						travelMode: window.google.maps.TravelMode.DRIVING,
						region: "za",
						unitSystem: window.google.maps.UnitSystem.METRIC,
					};

					this.directionsService.getDistanceMatrix(
						route,
						(response, status) => {
							if (status !== window.google.maps.DistanceMatrixStatus.OK) {
								// Error handling
								return;
							}

							this.reactiveValue = {
								...this.reactiveValue,
								address: this.componentToAddress(
									geocodedPlace.address_components,
								),
								distance: response.rows[0].elements[0].distance,
							};
						},
					);
				},
			);
		},
	},
	watch: {
		location: debounce(function (newValue) {
			// Clear stored delivery details if user edits away from confirmed selection
			if (this.reactiveValue && (!this.chosenLocation || newValue !== this.chosenLocation.description)) {
				this.reactiveValue = null;
			}
			if (newValue && this.autocompleteService) {
				this.autocompleteService.getPlacePredictions(
					{
						componentRestrictions: { country: "ZA" },
						input: newValue,
						types: ["address"],
						fields: ["place_id", "adr_address"],
					},
					this.displaySuggestions,
				);
			} else if (!newValue) {
				this.searchResults = [];
			}
		}, 750),
	},
	computed: {
		reactiveValue: {
			get() {
				return this.modelValue;
			},
			set(val) {
				this.$emit("update:modelValue", val);
			},
		},
	},
};
</script>

<style lang="scss" scoped>
.address-field-wrapper {
    position: relative;
    .autocomplete-results {
        position: absolute;
        box-sizing: border-box;
        top: 100%;
        left: 0;
        width: 100%;
        margin: 0;
        padding: 0;
        list-style-type: none;
        background-color: white;
        border-left: 1px solid $cl-primary;
        border-right: 1px solid $cl-primary;
        border-bottom: 1px solid $cl-primary;
        border-top: none;
        z-index: 5;
        opacity: 1;

        &.hidden {
            opacity: 0;
        }

        .autocomplete-result {
            line-height: 36px;
            padding: 0 10px;
            color: white;
            font-weight: 600;
            cursor: pointer;
            background-color: transparentize(black, 0.25);
            transition: background-color 100ms ease-out;

            &:hover {
                background-color: transparentize($cl-primary, 0);
            }
        }
    }
    input[type='text'].expanded {
        border-left: 1px solid $cl-primary;
        border-right: 1px solid $cl-primary;
        border-top: 1px solid $cl-primary;
    }
}
</style>